import { CANONICAL_TRAIT_TYPE_NAMES } from "./compositor.js";
import { buildTraitLayerImageCandidates } from "./traitAssetUrl.js";
import { getStakingEnv } from "./env.js";

const CANVAS_SIZE = 512;

/** @type {'custom-layers' | 'all-layers'} */
let activeLayerSource = "custom-layers";

/** @param {'custom-layers' | 'all-layers'} source */
export function setCosmeticLayerSource(source) {
  activeLayerSource = source === "all-layers" ? "all-layers" : "custom-layers";
}

export function getCosmeticLayerSource() {
  return activeLayerSource;
}

function composeOptions() {
  return {
    layerSource: activeLayerSource,
    allowCompositorFallback: activeLayerSource === "custom-layers",
  };
}

/**
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image load failed: ${url}`));
    img.src = url;
  });
}

/**
 * Rewrite external layer URLs to same-origin proxies so canvas stays exportable.
 * @param {string} url
 */
export function toCorsSafeImageUrl(url) {
  const raw = String(url ?? "").trim();
  if (!raw || raw.startsWith("data:")) return raw;

  const metaBase = getStakingEnv().metadataOrigin.replace(/\/+$/, "");
  if (metaBase && raw.startsWith(metaBase)) {
    const path = raw.slice(metaBase.length).replace(/^\//, "");
    if (path) return `/api/owlex-layer?path=${encodeURIComponent(path)}`;
  }

  if (typeof window !== "undefined") {
    try {
      const origin = window.location.origin;
      const abs = new URL(raw, origin);
      if (abs.origin === origin) {
        return abs.pathname + abs.search;
      }
    } catch {
      /* keep raw */
    }

    const compRaw = getStakingEnv().compositorUrl.replace(/\/+$/, "") || "/api/compositor";
    if (/^https?:\/\//i.test(compRaw) && raw.startsWith(compRaw)) {
      const sub = raw.slice(compRaw.length).replace(/^\//, "");
      if (sub) return `/api/compositor/${sub}`;
    }
  }

  return raw;
}

/**
 * @param {string[]} urls
 * @returns {Promise<HTMLImageElement | null>}
 */
async function loadFirstImage(urls) {
  for (const raw of urls) {
    const u = toCorsSafeImageUrl(raw);
    try {
      return await loadImage(u);
    } catch {
      /* try next */
    }
  }
  return null;
}


/**
 * Same-origin proxy avoids CORS blocks on metadata.owlex.art in the browser.
 * @param {string} tokenId
 */
export function owlexMetadataProxyUrl(tokenId) {
  const id = String(tokenId ?? "").trim();
  if (!/^\d+$/.test(id)) return "";
  return `/api/owlex-metadata/${id}`;
}

/**
 * @param {string} tokenId
 */
export async function fetchTokenMetadata(tokenId) {
  const id = String(tokenId ?? "").trim();
  if (!/^\d+$/.test(id)) return null;
  const base = getStakingEnv().metadataOrigin.replace(/\/+$/, "");
  const urls = [owlexMetadataProxyUrl(id), `${base}/metadata/${id}`];

  for (const url of urls) {
    if (!url) continue;
    try {
      const res = await fetch(url, { credentials: "omit", cache: "no-store" });
      if (!res.ok) continue;
      return await res.json();
    } catch {
      /* try next */
    }
  }
  return null;
}

/**
 * @param {string} s
 */
function normalizeTraitValue(s) {
  return String(s ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200b-\u200d\ufeff]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * @param {string} traitType
 * @returns {string | null}
 */
function attrToCanonical(traitType) {
  const t = String(traitType ?? "").trim();
  if (!t) return null;
  const hit = CANONICAL_TRAIT_TYPE_NAMES.find((c) => c.toLowerCase() === t.toLowerCase());
  return hit ?? null;
}

/**
 * Metadata attribute order = paint order (bottom → top). Matches compositor Worker.
 * @param {unknown[]} attrs
 */
function normalizeMetadataAttrs(attrs) {
  /** @type {{ trait_type: string, traitTypeId: number, value: string }[]} */
  const out = [];
  if (!Array.isArray(attrs)) return out;
  for (const a of attrs) {
    const canonical = attrToCanonical(a?.trait_type);
    if (!canonical) continue;
    const value = normalizeTraitValue(a?.value);
    if (!value) continue;
    const traitTypeId = CANONICAL_TRAIT_TYPE_NAMES.indexOf(canonical);
    out.push({ trait_type: canonical, traitTypeId, value });
  }
  return out;
}

/**
 * @param {{ trait_type: string, traitTypeId: number, value: string }[]} attrs
 * @param {number} traitTypeId
 * @param {string} newValue
 */
function applySwapToAttrs(attrs, traitTypeId, newValue) {
  const canonical = CANONICAL_TRAIT_TYPE_NAMES[traitTypeId];
  if (!canonical) return attrs;
  const next = normalizeTraitValue(newValue);
  if (!next) return attrs;
  let replaced = false;
  const out = attrs.map((a) => {
    if (a.trait_type !== canonical) return a;
    replaced = true;
    return { ...a, value: next };
  });
  if (!replaced) {
    out.push({ trait_type: canonical, traitTypeId, value: next });
  }
  return out;
}

/**
 * @param {unknown[]} attrs
 * @param {Record<number, string>} overrides
 */
function buildEffectiveAttributeStack(attrs, overrides) {
  let stack = normalizeMetadataAttrs(attrs);
  for (const [tidStr, raw] of Object.entries(overrides)) {
    const tid = Number(tidStr);
    const v = String(raw ?? "").trim();
    if (!Number.isFinite(tid) || tid < 0 || !v) continue;
    stack = applySwapToAttrs(stack, tid, v);
  }
  return stack;
}

/**
 * @param {string} typeName
 * @param {string} value
 * @param {'base' | 'swap' | 'single'} mode
 */
function layerImageCandidates(typeName, value, mode) {
  const all = buildTraitLayerImageCandidates(typeName, value, {
    layerSource: "all-layers",
    allowCompositorFallback: false,
  });
  if (mode === "base") return all;
  if (mode === "single") return buildTraitLayerImageCandidates(typeName, value, composeOptions());

  const custom = buildTraitLayerImageCandidates(typeName, value, {
    layerSource: "custom-layers",
    allowCompositorFallback: false,
  });
  /** Swapped inventory traits: custom-layers first, then all-layers (matches Worker). */
  const seen = new Set();
  const out = [];
  for (const u of [...custom, ...all]) {
    if (!seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}

/**
 * @param {string} tokenId
 * @param {Record<number, string>} overrides traitTypeId → trait value name
 * @param {HTMLCanvasElement} [target]
 * @param {{ baseLayerSource?: 'custom-layers' | 'all-layers', overrideLayerSource?: 'custom-layers' | 'all-layers' }} [options]
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function composeCosmeticPreview(tokenId, overrides = {}, target, options) {
  const meta = await fetchTokenMetadata(tokenId);
  const attrs = Array.isArray(meta?.attributes) ? meta.attributes : [];
  const hybrid =
    options != null &&
    (options.baseLayerSource != null || options.overrideLayerSource != null);

  const swappedIds = new Set(
    Object.entries(overrides)
      .filter(([, v]) => String(v ?? "").trim() !== "")
      .map(([k]) => Number(k))
      .filter((n) => Number.isFinite(n) && n >= 0)
  );

  const stack = buildEffectiveAttributeStack(attrs, overrides);

  const canvas = target ?? document.createElement("canvas");
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unsupported");
  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  for (const layer of stack) {
    const { trait_type: typeName, traitTypeId, value } = layer;
    if (!value) continue;

    let mode = "single";
    if (hybrid) {
      mode = swappedIds.has(traitTypeId) ? "swap" : "base";
    }

    const urls = layerImageCandidates(typeName, value, mode);
    const img = await loadFirstImage(urls);
    if (img) {
      ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
    }
  }

  return canvas;
}

/**
 * @param {string[]} tokenIds
 * @param {Record<number, string>} overrides
 * @param {(done: number, total: number) => void} [onProgress]
 * @returns {Promise<HTMLCanvasElement[]>}
 */
export async function composeCosmeticFrames(tokenIds, overrides, onProgress) {
  const frames = [];
  const total = tokenIds.length;
  for (let i = 0; i < total; ++i) {
    frames.push(await composeCosmeticPreview(tokenIds[i], overrides));
    onProgress?.(i + 1, total);
  }
  return frames;
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {string} filename
 * @returns {Promise<boolean>}
 */
export function downloadCanvasPng(canvas, filename) {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        resolve(true);
      }, "image/png");
    } catch {
      resolve(false);
    }
  });
}
